import React, { useState, useEffect } from 'react';
import { supabase } from './supabase';

// Tecovas brand colors
const B = {
  forest: '#1B3D2F',
  cream: '#F5F1E8',
  sand: '#E5DED3',
  rust: '#B85C38',
  olive: '#5C6B4A',
  stone: '#8B8680',
  sage: '#9CAF88'
};

// Allowed email domain
const ALLOWED_DOMAIN = 'tecovas.com';

export default function Auth({ onLogin }) {
  // View states: 'login', 'signup', 'forgot', 'reset-sent', 'update-password'
  const [view, setView] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [hasSession, setHasSession] = useState(false);

  // Check if this is a password reset callback
  useEffect(() => {
    const checkForRecovery = async () => {
      // Check URL for reset password indicators
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const type = hashParams.get('type');
      const accessToken = hashParams.get('access_token');
      
      const isResetPage = window.location.pathname.includes('reset-password');
      const isRecoveryFlow = type === 'recovery' || (isResetPage && accessToken);

      if (isRecoveryFlow) {
        // Try to exchange the token for a session
        if (accessToken) {
          try {
            const { data, error } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: hashParams.get('refresh_token') || '',
            });
            if (!error && data.session) {
              setHasSession(true);
              setView('update-password');
              return;
            }
          } catch (e) {
            console.error('Session error:', e);
          }
        }
        
        // Check if we already have a valid session from the recovery
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          setHasSession(true);
          setView('update-password');
          return;
        }

        // Show the update password form anyway (might work if session is valid)
        setView('update-password');
      }
    };

    checkForRecovery();

    // Listen for PASSWORD_RECOVERY event
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setHasSession(true);
        setView('update-password');
      }
      if (event === 'SIGNED_IN' && view === 'update-password') {
        setHasSession(true);
      }
    });

    return () => subscription.unsubscribe();
  }, [view]);

  // Password validation
  const passwordChecks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
  };
  const passwordValid = Object.values(passwordChecks).every(Boolean);
  const passwordsMatch = password === confirmPassword;

  // Check if email domain is allowed
  const isAllowedDomain = (email) => {
    const domain = email.split('@')[1]?.toLowerCase();
    return domain === ALLOWED_DOMAIN;
  };

  const clearForm = () => {
    setError(null);
    setMessage(null);
    setPassword('');
    setConfirmPassword('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
      onLogin(data.user);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validate domain
    if (!isAllowedDomain(email)) {
      setError(`Only @${ALLOWED_DOMAIN} email addresses are allowed.`);
      setLoading(false);
      return;
    }

    // Validate password strength
    if (!passwordValid) {
      setError('Please meet all password requirements.');
      setLoading(false);
      return;
    }

    // Validate passwords match
    if (!passwordsMatch) {
      setError('Passwords do not match.');
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
      });
      if (error) throw error;
      if (data.user && !data.session) {
        setMessage('Check your email for the confirmation link!');
      } else {
        onLogin(data.user);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!email) {
      setError('Please enter your email address.');
      setLoading(false);
      return;
    }

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      setView('reset-sent');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validate password strength
    if (!passwordValid) {
      setError('Please meet all password requirements.');
      setLoading(false);
      return;
    }

    // Validate passwords match
    if (!passwordsMatch) {
      setError('Passwords do not match.');
      setLoading(false);
      return;
    }

    try {
      const { error } = await supabase.auth.updateUser({
        password: password
      });
      if (error) throw error;
      
      // Sign out after password update so they can log in fresh
      await supabase.auth.signOut();
      
      // Clear the URL hash/params
      window.history.replaceState({}, document.title, '/');
      
      setMessage('Password updated successfully! Please sign in with your new password.');
      setPassword('');
      setConfirmPassword('');
      setView('login');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Password requirement indicator
  const PasswordCheck = ({ passed, label }) => (
    <div className="flex items-center gap-2 text-xs">
      <div 
        className="w-4 h-4 rounded-full flex items-center justify-center"
        style={{ 
          backgroundColor: passed ? B.olive : B.sand,
          transition: 'background-color 0.2s'
        }}
      >
        {passed && (
          <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          </svg>
        )}
      </div>
      <span style={{ color: passed ? B.olive : B.stone }}>{label}</span>
    </div>
  );

  // Eye icon for show/hide password
  const EyeIcon = ({ show }) => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: B.stone }}>
      {show ? (
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
      ) : (
        <>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </>
      )}
    </svg>
  );

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: B.cream }}>
      <div className="w-full max-w-md px-4">
        {/* Logo / Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ backgroundColor: B.forest }}>
            <svg viewBox="0 0 24 24" className="w-8 h-8 text-white" fill="currentColor">
              <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="currentColor" strokeWidth="2" fill="none"/>
            </svg>
          </div>
          <h1 className="text-2xl font-bold tracking-wide" style={{ color: B.forest, fontFamily: 'Gill Sans, sans-serif' }}>
            TECOVAS
          </h1>
          <p className="text-sm mt-1" style={{ color: B.stone, fontFamily: 'Gill Sans, sans-serif' }}>
            Homestead SCEL — Supply Chain Execution Layer
          </p>
        </div>

        {/* Auth Card */}
        <div className="rounded-xl p-8 shadow-lg" style={{ backgroundColor: '#fff', border: `1px solid ${B.sand}` }}>
          
          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 rounded-lg text-sm" style={{ backgroundColor: B.rust + '15', color: B.rust, border: `1px solid ${B.rust}30` }}>
              {error}
            </div>
          )}

          {/* Success Message */}
          {message && (
            <div className="mb-4 p-3 rounded-lg text-sm" style={{ backgroundColor: B.olive + '15', color: B.olive, border: `1px solid ${B.olive}30` }}>
              {message}
            </div>
          )}

          {/* LOGIN VIEW */}
          {view === 'login' && (
            <>
              <h2 className="text-lg font-semibold mb-6 text-center" style={{ color: B.forest, fontFamily: 'Gill Sans, sans-serif' }}>
                Howdy, Partner
              </h2>
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: B.forest, fontFamily: 'Gill Sans, sans-serif' }}>
                    Email
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    className="w-full px-4 py-3 rounded-lg text-sm outline-none transition-all"
                    style={{ backgroundColor: B.cream, border: `1px solid ${B.sand}`, color: B.forest, fontFamily: 'Gill Sans, sans-serif' }}
                    onFocus={(e) => e.target.style.borderColor = B.forest}
                    onBlur={(e) => e.target.style.borderColor = B.sand}
                    placeholder="you@tecovas.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: B.forest, fontFamily: 'Gill Sans, sans-serif' }}>
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoComplete="current-password"
                      className="w-full px-4 py-3 pr-12 rounded-lg text-sm outline-none transition-all"
                      style={{ backgroundColor: B.cream, border: `1px solid ${B.sand}`, color: B.forest, fontFamily: 'Gill Sans, sans-serif' }}
                      onFocus={(e) => e.target.style.borderColor = B.forest}
                      onBlur={(e) => e.target.style.borderColor = B.sand}
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1"
                      tabIndex={-1}
                    >
                      <EyeIcon show={showPassword} />
                    </button>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => { clearForm(); setView('forgot'); }}
                    className="text-xs hover:underline"
                    style={{ color: B.olive, fontFamily: 'Gill Sans, sans-serif', background: 'none', border: 'none', cursor: 'pointer' }}
                  >
                    Forgot password?
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 rounded-lg text-sm font-semibold uppercase tracking-wide transition-all"
                  style={{ backgroundColor: loading ? B.stone : B.forest, color: '#fff', fontFamily: 'Gill Sans, sans-serif', cursor: loading ? 'not-allowed' : 'pointer' }}
                >
                  {loading ? 'Saddling up...' : 'Saddle Up'}
                </button>
              </form>

              <div className="mt-6 text-center">
                <span className="text-sm" style={{ color: B.stone, fontFamily: 'Gill Sans, sans-serif' }}>
                  Don't have an account?{' '}
                </span>
                <button
                  type="button"
                  onClick={() => { clearForm(); setView('signup'); }}
                  className="text-sm font-medium hover:underline"
                  style={{ color: B.olive, fontFamily: 'Gill Sans, sans-serif', background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  Sign up
                </button>
              </div>
            </>
          )}

          {/* SIGNUP VIEW */}
          {view === 'signup' && (
            <>
              <h2 className="text-lg font-semibold mb-6 text-center" style={{ color: B.forest, fontFamily: 'Gill Sans, sans-serif' }}>
                Create Account
              </h2>
              <form onSubmit={handleSignup} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: B.forest, fontFamily: 'Gill Sans, sans-serif' }}>
                    Work Email
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="off"
                    className="w-full px-4 py-3 rounded-lg text-sm outline-none transition-all"
                    style={{ backgroundColor: B.cream, border: `1px solid ${B.sand}`, color: B.forest, fontFamily: 'Gill Sans, sans-serif' }}
                    onFocus={(e) => e.target.style.borderColor = B.forest}
                    onBlur={(e) => e.target.style.borderColor = B.sand}
                    placeholder="you@tecovas.com"
                  />
                  <p className="text-xs mt-1" style={{ color: B.stone }}>
                    Only @tecovas.com emails allowed
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: B.forest, fontFamily: 'Gill Sans, sans-serif' }}>
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoComplete="off"
                      className="w-full px-4 py-3 pr-12 rounded-lg text-sm outline-none transition-all"
                      style={{ backgroundColor: B.cream, border: `1px solid ${B.sand}`, color: B.forest, fontFamily: 'Gill Sans, sans-serif' }}
                      onFocus={(e) => e.target.style.borderColor = B.forest}
                      onBlur={(e) => e.target.style.borderColor = B.sand}
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1"
                      tabIndex={-1}
                    >
                      <EyeIcon show={showPassword} />
                    </button>
                  </div>
                  
                  {/* Password Requirements */}
                  <div className="mt-3 p-3 rounded-lg space-y-2" style={{ backgroundColor: B.cream }}>
                    <p className="text-xs font-medium mb-2" style={{ color: B.forest }}>Password must have:</p>
                    <PasswordCheck passed={passwordChecks.length} label="At least 8 characters" />
                    <PasswordCheck passed={passwordChecks.uppercase} label="One uppercase letter" />
                    <PasswordCheck passed={passwordChecks.lowercase} label="One lowercase letter" />
                    <PasswordCheck passed={passwordChecks.number} label="One number" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: B.forest, fontFamily: 'Gill Sans, sans-serif' }}>
                    Confirm Password
                  </label>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    autoComplete="off"
                    className="w-full px-4 py-3 rounded-lg text-sm outline-none transition-all"
                    style={{ 
                      backgroundColor: B.cream, 
                      border: `1px solid ${confirmPassword && !passwordsMatch ? B.rust : B.sand}`, 
                      color: B.forest, 
                      fontFamily: 'Gill Sans, sans-serif' 
                    }}
                    onFocus={(e) => e.target.style.borderColor = B.forest}
                    onBlur={(e) => e.target.style.borderColor = confirmPassword && !passwordsMatch ? B.rust : B.sand}
                    placeholder="••••••••"
                  />
                  {confirmPassword && !passwordsMatch && (
                    <p className="text-xs mt-1" style={{ color: B.rust }}>Passwords do not match</p>
                  )}
                  {confirmPassword && passwordsMatch && (
                    <p className="text-xs mt-1" style={{ color: B.olive }}>Passwords match ✓</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading || !passwordValid || !passwordsMatch}
                  className="w-full py-3 rounded-lg text-sm font-semibold uppercase tracking-wide transition-all"
                  style={{ 
                    backgroundColor: (loading || !passwordValid || !passwordsMatch) ? B.stone : B.forest, 
                    color: '#fff', 
                    fontFamily: 'Gill Sans, sans-serif', 
                    cursor: (loading || !passwordValid || !passwordsMatch) ? 'not-allowed' : 'pointer' 
                  }}
                >
                  {loading ? 'Creating account...' : 'Create Account'}
                </button>
              </form>

              <div className="mt-6 text-center">
                <span className="text-sm" style={{ color: B.stone, fontFamily: 'Gill Sans, sans-serif' }}>
                  Already have an account?{' '}
                </span>
                <button
                  type="button"
                  onClick={() => { clearForm(); setView('login'); }}
                  className="text-sm font-medium hover:underline"
                  style={{ color: B.olive, fontFamily: 'Gill Sans, sans-serif', background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  Sign in
                </button>
              </div>
            </>
          )}

          {/* FORGOT PASSWORD VIEW */}
          {view === 'forgot' && (
            <>
              <h2 className="text-lg font-semibold mb-2 text-center" style={{ color: B.forest, fontFamily: 'Gill Sans, sans-serif' }}>
                Reset Password
              </h2>
              <p className="text-sm text-center mb-6" style={{ color: B.stone, fontFamily: 'Gill Sans, sans-serif' }}>
                Enter your email and we'll send you a link to reset your password.
              </p>
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: B.forest, fontFamily: 'Gill Sans, sans-serif' }}>
                    Email
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    className="w-full px-4 py-3 rounded-lg text-sm outline-none transition-all"
                    style={{ backgroundColor: B.cream, border: `1px solid ${B.sand}`, color: B.forest, fontFamily: 'Gill Sans, sans-serif' }}
                    onFocus={(e) => e.target.style.borderColor = B.forest}
                    onBlur={(e) => e.target.style.borderColor = B.sand}
                    placeholder="you@tecovas.com"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 rounded-lg text-sm font-semibold uppercase tracking-wide transition-all"
                  style={{ backgroundColor: loading ? B.stone : B.forest, color: '#fff', fontFamily: 'Gill Sans, sans-serif', cursor: loading ? 'not-allowed' : 'pointer' }}
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </button>
              </form>

              <div className="mt-6 text-center">
                <button
                  type="button"
                  onClick={() => { clearForm(); setView('login'); }}
                  className="text-sm font-medium hover:underline"
                  style={{ color: B.olive, fontFamily: 'Gill Sans, sans-serif', background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  ← Back to sign in
                </button>
              </div>
            </>
          )}

          {/* RESET EMAIL SENT VIEW */}
          {view === 'reset-sent' && (
            <>
              <div className="text-center py-4">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ backgroundColor: B.olive + '20' }}>
                  <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke={B.olive}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h2 className="text-lg font-semibold mb-2" style={{ color: B.forest, fontFamily: 'Gill Sans, sans-serif' }}>
                  Check Your Email
                </h2>
                <p className="text-sm mb-6" style={{ color: B.stone, fontFamily: 'Gill Sans, sans-serif' }}>
                  We've sent a password reset link to<br />
                  <strong style={{ color: B.forest }}>{email}</strong>
                </p>
                <p className="text-xs mb-6" style={{ color: B.stone, fontFamily: 'Gill Sans, sans-serif' }}>
                  Didn't receive the email? Check your spam folder or{' '}
                  <button
                    type="button"
                    onClick={() => setView('forgot')}
                    className="hover:underline"
                    style={{ color: B.olive, background: 'none', border: 'none', cursor: 'pointer' }}
                  >
                    try again
                  </button>
                </p>
              </div>

              <button
                type="button"
                onClick={() => { clearForm(); setView('login'); }}
                className="w-full py-3 rounded-lg text-sm font-semibold uppercase tracking-wide transition-all"
                style={{ backgroundColor: B.forest, color: '#fff', fontFamily: 'Gill Sans, sans-serif', cursor: 'pointer' }}
              >
                Back to Sign In
              </button>
            </>
          )}

          {/* UPDATE PASSWORD VIEW (after clicking reset link) */}
          {view === 'update-password' && (
            <>
              {!hasSession && (
                <div className="mb-4 p-3 rounded-lg text-sm" style={{ backgroundColor: B.rust + '15', color: B.rust, border: `1px solid ${B.rust}30` }}>
                  Auth session missing! Please request a new password reset link.
                </div>
              )}
              <h2 className="text-lg font-semibold mb-2 text-center" style={{ color: B.forest, fontFamily: 'Gill Sans, sans-serif' }}>
                Set New Password
              </h2>
              <p className="text-sm text-center mb-6" style={{ color: B.stone, fontFamily: 'Gill Sans, sans-serif' }}>
                Enter your new password below.
              </p>
              <form onSubmit={handleUpdatePassword} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: B.forest, fontFamily: 'Gill Sans, sans-serif' }}>
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoComplete="new-password"
                      className="w-full px-4 py-3 pr-12 rounded-lg text-sm outline-none transition-all"
                      style={{ backgroundColor: B.cream, border: `1px solid ${B.sand}`, color: B.forest, fontFamily: 'Gill Sans, sans-serif' }}
                      onFocus={(e) => e.target.style.borderColor = B.forest}
                      onBlur={(e) => e.target.style.borderColor = B.sand}
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1"
                      tabIndex={-1}
                    >
                      <EyeIcon show={showPassword} />
                    </button>
                  </div>
                  
                  {/* Password Requirements */}
                  <div className="mt-3 p-3 rounded-lg space-y-2" style={{ backgroundColor: B.cream }}>
                    <p className="text-xs font-medium mb-2" style={{ color: B.forest }}>Password must have:</p>
                    <PasswordCheck passed={passwordChecks.length} label="At least 8 characters" />
                    <PasswordCheck passed={passwordChecks.uppercase} label="One uppercase letter" />
                    <PasswordCheck passed={passwordChecks.lowercase} label="One lowercase letter" />
                    <PasswordCheck passed={passwordChecks.number} label="One number" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: B.forest, fontFamily: 'Gill Sans, sans-serif' }}>
                    Confirm New Password
                  </label>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    className="w-full px-4 py-3 rounded-lg text-sm outline-none transition-all"
                    style={{ 
                      backgroundColor: B.cream, 
                      border: `1px solid ${confirmPassword && !passwordsMatch ? B.rust : B.sand}`, 
                      color: B.forest, 
                      fontFamily: 'Gill Sans, sans-serif' 
                    }}
                    onFocus={(e) => e.target.style.borderColor = B.forest}
                    onBlur={(e) => e.target.style.borderColor = confirmPassword && !passwordsMatch ? B.rust : B.sand}
                    placeholder="••••••••"
                  />
                  {confirmPassword && !passwordsMatch && (
                    <p className="text-xs mt-1" style={{ color: B.rust }}>Passwords do not match</p>
                  )}
                  {confirmPassword && passwordsMatch && (
                    <p className="text-xs mt-1" style={{ color: B.olive }}>Passwords match ✓</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading || !passwordValid || !passwordsMatch || !hasSession}
                  className="w-full py-3 rounded-lg text-sm font-semibold uppercase tracking-wide transition-all"
                  style={{ 
                    backgroundColor: (loading || !passwordValid || !passwordsMatch || !hasSession) ? B.stone : B.forest, 
                    color: '#fff', 
                    fontFamily: 'Gill Sans, sans-serif', 
                    cursor: (loading || !passwordValid || !passwordsMatch || !hasSession) ? 'not-allowed' : 'pointer' 
                  }}
                >
                  {loading ? 'Updating...' : 'Update Password'}
                </button>
              </form>

              <div className="mt-6 text-center">
                <button
                  type="button"
                  onClick={() => { 
                    clearForm(); 
                    window.history.replaceState({}, document.title, '/');
                    setView('forgot'); 
                  }}
                  className="text-sm font-medium hover:underline"
                  style={{ color: B.olive, fontFamily: 'Gill Sans, sans-serif', background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  ← Request new reset link
                </button>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <p className="text-center mt-6 text-xs" style={{ color: B.stone, fontFamily: 'Gill Sans, sans-serif' }}>
          © 2026 Tecovas Supply Chain Team
        </p>
      </div>
    </div>
  );
}
